import axios from "axios";
import { API_BASE_URL } from "../config";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo eliminar token si es un error de autenticación específico
    if (
      error.response?.status === 401 &&
      error.response?.data?.message !== "Token no proporcionado"
    ) {
      // Verificar si estamos en una ruta que no sea login para evitar loops
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  getCurrentUser: () => api.get("/auth/me"),
  verifyToken: async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;

      const response = await api.get("/auth/me");
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },
};

// Users services
export const userService = {
  getAll: () => api.get("/usuarios"),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (userData) => api.post("/usuarios", userData),
  update: (id, userData) => api.put(`/usuarios/${id}`, userData),
  changePassword: (id, password) =>
    api.put(`/usuarios/${id}/password`, { password }),
  toggleStatus: (id) => api.put(`/usuarios/${id}/toggle-status`),
};

// Gastos services
export const gastosService = {
  getAll: (params) => api.get("/gastos", { params }),
  getById: (id) => api.get(`/gastos/${id}`),
  create: (gastoData) => api.post("/gastos", gastoData),
  updateStatus: (id, statusData) => api.put(`/gastos/${id}/estado`, statusData),
  getTipos: () => api.get("/gastos/tipos/list"),
  getEmpleados: () => api.get("/gastos/empleados/list"),
  delete: (id) => api.delete(`/gastos/${id}`),
  updateGasto: (id, data) => api.put(`/gastos/${id}/editar`, data),
  exportarExcel: (fechaDesde, fechaHasta) =>
    api.get(
      `/gastos/exportar-excel?fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}`,
      {
        responseType: "blob",
      }
    ),
  getJornadas: (params) => api.get("/gastos/jornadas", { params }),
  getGastosPendingCount: (params) => api.get("/gastos/pending-count", { params }),
  getJornadasPendingCount: (params) => api.get("/gastos/jornadas/pending-count", { params }),
  validarJornadas: (items) => api.post("/gastos/jornadas/validar", { items }),
  getJornadaShifts: (factorialEmployeeId, date) =>
    api.get("/gastos/jornadas/shifts", { params: { factorialEmployeeId, date } }),
};

// Empleados services
export const empleadosService = {
  buscar: (params) => api.get("/gastos/empleados/buscar", { params }),
  getDepartamentos: () => api.get("/gastos/departamentos/list"),
  enviarRecordatorio: (crewId) =>
    api.post("/auth/enviar-mensaje-validar-gastos", { crewId }),
  getBalanceHoras: (year, empleado) =>
    api.get("/gastos/empleados/getBalanceHoras", {
      params: { year, empleado },
    }),
  exportarBalanceHoras: (year) =>
    api.get("/gastos/exportar-balance-horas", {
      params: { year },
      responseType: "blob",
    }),
  toggleActive: (crewId, active) =>
    api.post("/gastos/empleados/toggle-active", { crewId, active }),
};

// Administradores services
export const administradoresService = {
  buscar: (params) => api.get("/administradores/buscar", { params }),
  create: (data) => api.post("/administradores", data),
  update: (id, data) => api.put(`/administradores/${id}`, data),
  delete: (id) => api.delete(`/administradores/${id}`),
};

/**
 * Sincronización fichajes Factorial ↔ Rentman.
 * getAll: listado filtrado; getIds: IDs de toda la busqueda (select-all);
 * getEmpleados: combo; reimport: uno a uno.
 */
export const fichajesService = {
  getAll: (params) => api.get("/fichajes", { params }),
  getIds: (params) => api.get("/fichajes/ids", { params }),
  getEmpleados: () => api.get("/fichajes/empleados"),
  reimport: (id) => api.post(`/fichajes/${id}/reimport`),
};

export default api;
